import {
    FormControl,
    FormErrorMessage,
    FormLabel,
    Stack,
    Button,
    Heading,
    Card,
    CardBody
} from '@chakra-ui/react'
import { useMemo } from 'react'
import { Controller } from 'react-hook-form'
import FormField from '@/components/FormField'
import Countries from '@/shared/config/listOfCountries.json'
import ReactSelect from 'react-select'
import { useBranches } from '@/shared/api/hooks/useBranches'
import useProfileUpdate from '../hooks/useProfileUpdate'
import { SelectOption } from '../type'
import { useParentProfileQuery } from '..'

export default function ProfileForm() {
    const { parentProfile } = useParentProfileQuery()

    const { branches, loading: branchLoading } = useBranches()
    const countryOptions: SelectOption[] = useMemo(
        () =>
            Countries.map((item: any) => ({
                value: item.name,
                label: item.name
            })),
        []
    )

    const { handleSubmit, register, errors, control, isUpdating } = useProfileUpdate({
        initialData: parentProfile
    })

    const onSubmit = async () => {
        await handleSubmit()
    }

    return (
        <>
            <Heading size="sm" mt="1em" mb={4}>
                Personal Information
            </Heading>
            <Card variant="outline">
                <CardBody>
                    <Stack spacing={4}>
                        <Stack direction={['column', 'row']}>
                            <FormField
                                label="First Name"
                                name="firstName"
                                type="text"
                                required
                                register={register}
                                error={errors.firstName}
                            />
                            <FormField
                                label="Last Name"
                                name="lastName"
                                type="text"
                                register={register}
                                error={errors.lastName}
                            />
                        </Stack>

                        <Stack direction={['column', 'row']}>
                            <FormField
                                label="Phone Number"
                                name="phoneNumber"
                                type="tel"
                                required
                                register={register}
                                error={errors.phoneNumber}
                            />
                            <FormField
                                label="Relationship"
                                name="relationship"
                                type="select"
                                required
                                register={register}
                                error={errors.relationship}
                            >
                                <option value="Father">Father</option>
                                <option value="Mother">Mother</option>
                                <option value="N/A">N/A</option>
                            </FormField>
                        </Stack>

                        <FormControl isInvalid={Boolean(errors.branch)} isRequired>
                            <FormLabel>Branch</FormLabel>
                            <Controller
                                name="branch"
                                control={control}
                                render={({ field }) => (
                                    <ReactSelect
                                        {...field}
                                        isSearchable
                                        placeholder="Choose branch"
                                        options={branches}
                                        isLoading={branchLoading}
                                    />
                                )}
                            />
                            <FormErrorMessage>
                                {errors.branch && errors.branch.message}
                            </FormErrorMessage>
                        </FormControl>

                        <Stack direction={['column', 'row']}>
                            <FormField
                                label="Address"
                                name="address"
                                type="text"
                                register={register}
                                error={errors.address}
                            />
                            <FormField
                                label="City"
                                name="city"
                                type="text"
                                register={register}
                                error={errors.city}
                            />
                        </Stack>

                        <Stack direction={['column', 'row']}>
                            <FormField
                                label="State"
                                name="state"
                                type="text"
                                required={false}
                                register={register}
                                error={errors.state}
                            />

                            <FormControl isInvalid={Boolean(errors.country)}>
                                <FormLabel>Country</FormLabel>
                                <Controller
                                    name="country"
                                    control={control}
                                    render={({ field }) => (
                                        <ReactSelect
                                            {...field}
                                            isSearchable
                                            placeholder="Choose country"
                                            options={countryOptions}
                                        />
                                    )}
                                />
                                <FormErrorMessage>
                                    {errors.country && errors.country.message}
                                </FormErrorMessage>
                            </FormControl>
                        </Stack>

                        <Stack spacing={10} pt={2}>
                            <Button
                                type="submit"
                                loadingText="Submitting"
                                size="lg"
                                isLoading={isUpdating}
                                onClick={onSubmit}
                            >
                                Update Account
                            </Button>
                        </Stack>
                    </Stack>
                </CardBody>
            </Card>
        </>
    )
}
